// src/services/evidenceWebService.js
/**
 * Evidence Web Service
 *
 * Web-side evidence operations for PM/PC approval workflow.
 * Separate from mobile EvidenceController (which handles supervisor uploads).
 *
 * Backend: MilestoneEvidenceController.java
 * Base paths: /api/evidence, /api/milestones/{id}/evidence
 *
 * REQUIRES backend endpoint (added in Phase 4):
 *   GET /api/milestones/{id}/evidence
 *   → Returns all evidence for a milestone grouped by definition
 */

import api from '../config/axios';

// ============================================================================
// QUERY OPERATIONS
// ============================================================================

/**
 * GET /api/milestones/{milestoneId}/evidence
 *
 * All evidence for a milestone (direct FK — works for all upload sources).
 * Returns flat list, grouped by definition on frontend via groupByDefinition().
 *
 * @param {number} milestoneId
 * @returns {Promise<EvidenceDetailResponse[]>}
 */
export const getEvidenceByMilestone = async (milestoneId) => {
  const res = await api.get(`/milestones/${milestoneId}/evidence`);
  return res.data;
};

/**
 * GET /api/milestones/{milestoneId}/evidence/requirements
 *
 * Evidence requirements tree (definitions + submitted counts + completion status).
 * Used for the evidence panel in FieldSubmissionDetail.
 *
 * @param {number} milestoneId
 * @returns {Promise<EvidenceRequirementsResponse>}
 */
export const getEvidenceRequirements = async (milestoneId) => {
  const res = await api.get(`/milestones/${milestoneId}/evidence/requirements`);
  return res.data;
};

// ============================================================================
// APPROVAL OPERATIONS
// ============================================================================

/**
 * PUT /api/evidence/{evidenceId}/approve
 *
 * Approve a single evidence file.
 * @param {number} evidenceId
 * @param {string} [notes] Optional approval notes
 */
export const approveEvidence = async (evidenceId, notes = '') => {
  const res = await api.put(`/evidence/${evidenceId}/approve`, null, {
    params: { notes },
  });
  return res.data;
};

/**
 * PUT /api/evidence/{evidenceId}/reject
 *
 * Reject a single evidence file.
 * Rejection reason is shown to supervisor as guidance for re-upload.
 *
 * @param {number} evidenceId
 * @param {string} reason  Mandatory — explain why the file is rejected
 */
export const rejectEvidence = async (evidenceId, reason) => {
  if (!reason?.trim()) throw new Error('Rejection reason is required');
  const res = await api.put(`/evidence/${evidenceId}/reject`, null, {
    params: { reason },
  });
  return res.data;
};

/**
 * POST /api/evidence/bulk-approve
 *
 * Approve multiple evidence files at once.
 * @param {number[]} evidenceIds
 * @param {string} [notes]
 */
export const bulkApproveEvidence = async (evidenceIds, notes = '') => {
  const res = await api.post('/evidence/bulk-approve', evidenceIds, {
    params: { notes },
  });
  return res.data;
};

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export const EvidenceApprovalStatus = {
  PENDING:  'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
};

export const EvidenceType = {
  PHOTO:    'PHOTO',
  DOCUMENT: 'DOCUMENT',
  VIDEO:    'VIDEO',
  OTHER:    'OTHER',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get Tailwind classes for evidence approval status badge.
 */
export const getEvidenceStatusBadgeClass = (status) => ({
  PENDING:  'bg-amber-100 text-amber-700 border-amber-200',
  APPROVED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  REJECTED: 'bg-red-100 text-red-700 border-red-200',
}[status] ?? 'bg-gray-100 text-gray-600 border-gray-200');

export const getEvidenceStatusLabel = (status) => ({
  PENDING:  'Pending',
  APPROVED: 'Disetujui',
  REJECTED: 'Ditolak',
}[status] ?? status);

/**
 * Group flat evidence list by evidenceDefinitionId.
 * Returns array of definition groups, each with a files[] array.
 *
 * @param {EvidenceDetailResponse[]} evidenceList
 * @param {object[]} [definitions] Optional definitions for enrichment
 * @returns {DefinitionGroup[]}
 */
export const groupByDefinition = (evidenceList, definitions = []) => {
  const defMap = new Map(definitions.map(d => [d.definitionId, d]));
  const groups = new Map();

  (evidenceList ?? []).forEach((ev) => {
    const key = ev.evidenceDefinitionId ?? 'general';
    if (!groups.has(key)) {
      const def = defMap.get(ev.evidenceDefinitionId) ?? {};
      groups.set(key, {
        definitionId:   ev.evidenceDefinitionId ?? null,
        definitionName: ev.definitionName ?? 'General Evidence',
        description:    ev.definitionDescription ?? '',
        isMandatory:    ev.isMandatory ?? false,
        evidenceType:   ev.evidenceType ?? 'PHOTO',
        minFileCount:   def.minFileCount ?? null,
        maxFileCount:   def.maxFileCount ?? null,
        files: [],
      });
    }
    groups.get(key).files.push(ev);
  });

  return Array.from(groups.values());
};

/**
 * Compute approval summary for a definition group.
 */
export const computeGroupSummary = (group) => {
  const counts = { PENDING: 0, APPROVED: 0, REJECTED: 0 };
  group.files.forEach(f => { counts[f.approvalStatus] = (counts[f.approvalStatus] ?? 0) + 1; });

  const isSatisfied = (() => {
    const approved = counts.APPROVED;
    const min = group.minFileCount;
    const max = group.maxFileCount;
    if (min !== null && approved < min) return false;
    if (max !== null && approved > max) return false;
    return true;
  })();

  return {
    ...counts,
    total: group.files.length,
    isSatisfied,
    hasRejected: counts.REJECTED > 0,
    allApproved: counts.APPROVED === group.files.length && group.files.length > 0,
  };
};

/**
 * Compute overall evidence summary for a milestone.
 * Returns { totalFiles, pending, approved, rejected, hasRejected, isComplete }
 */
export const computeEvidenceSummary = (evidenceList) => {
  const summary = { totalFiles: 0, pending: 0, approved: 0, rejected: 0 };
  (evidenceList ?? []).forEach(ev => {
    summary.totalFiles++;
    summary[ev.approvalStatus?.toLowerCase()]++;
  });
  summary.hasRejected = summary.rejected > 0;
  summary.isComplete  = summary.pending === 0 && summary.totalFiles > 0;
  return summary;
};

export default {
  getEvidenceByMilestone,
  getEvidenceRequirements,
  approveEvidence,
  rejectEvidence,
  bulkApproveEvidence,
  EvidenceApprovalStatus,
  EvidenceType,
  getEvidenceStatusBadgeClass,
  getEvidenceStatusLabel,
  groupByDefinition,
  computeGroupSummary,
  computeEvidenceSummary,
};